import React from 'react';
import { useTranslation } from 'react-i18next';
import { MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { StatusPill } from '../../../../theme/components/StatusPill';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';
import { INVOICE_STATUS_KEYS, INVOICE_STATUS_PILL, invoiceStatusOf } from '../../../../functions/CreditorInvoices.Functions';
import { CreditorInvoice } from '../../../../models/Entities/CashFlow/CreditorInvoice/CreditorInvoice';
import { formatCurrencyAmountWithIssue } from '../../../../commons/currency/Currency.Functions';

/** Textos de facturas y comprobantes: todas las claves cuelgan de `creditorInvoices`. */
export const useInvoicesText = () => {
    const [t] = useTranslation();
    return (key: string, options?: Record<string, unknown>): string => t(`creditorInvoices.${key}`, options as any) as string;
};

export const fmtInvoiceDate = (value: Date | string | null | undefined): string => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    // Las fechas de factura llegan como "2026-10-01" (sin hora): se muestran tal cual, sin correrlas por la zona horaria.
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-');
        return `${day}/${month}/${year}`;
    }
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const fmtInvoiceAmount = (amount: number | null | undefined, currency: string | null | undefined): string =>
    amount === null || amount === undefined ? '—' : formatCurrencyAmountWithIssue(amount, (currency || '').toUpperCase() || undefined);

/** Pastilla de estado de la factura, con símbolo (el estado nunca depende sólo del color). */
export const InvoiceStatusPill = ({ invoice, className }: { invoice: CreditorInvoice; className?: string }) => {
    const tr = useInvoicesText();
    const status = invoiceStatusOf(invoice);
    const pill = INVOICE_STATUS_PILL[status];
    return (
        <StatusPill tone={pill.tone} symbol={pill.symbol} className={className} data-testid={`invoice-status-${invoice.id}`}>
            {tr(`status.${INVOICE_STATUS_KEYS[status]}`)}
        </StatusPill>
    );
};

/** Endpoint de CashFlow todavía no publicado (404): "Dato no disponible todavía". */
export const UnavailableNotice = ({ what, testId }: { what: string; testId?: string }) => {
    const [t] = useTranslation();
    return (
        <GuideMessageBar intent="info" layout="multiline" data-testid={testId}>
            <MessageBarBody>
                <MessageBarTitle>{t('practitionerAccountStatement.states.unavailableTitle') as string}</MessageBarTitle>
                {t('practitionerAccountStatement.states.unavailableText', { what }) as string}
            </MessageBarBody>
        </GuideMessageBar>
    );
};
